import { Breadcrumbs, Loader, Menu, Modal, Tabs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconAlertTriangle,
    IconDatabase,
    IconDots,
    IconFolderShare,
    IconHammer,
    IconLayoutDashboard,
    IconRefresh,
    IconReload,
    IconRepeat,
    IconSettings,
    IconTerminal2,
    IconTrash,
    IconWorld,
} from '@tabler/icons-react';
import { AccessBadge, Avatar, Button } from '@xver/components/Atoms';
import { useActiveProject, useProductAccess } from '@xver/hooks/useActiveProject';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import { focusRowMenu, moveTriggerProps, useMoveResource } from '@xver/hooks/useMoveResource';
import usePageHeading from '@xver/hooks/usePageHeading';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import ContainerStatusBadge from '../ContainerEnginePage/components/ContainerStatusBadge';
import BuildsTabPanel from './components/BuildsTabPanel';
import DomainsTabPanel from './components/DomainsTabPanel';
import LogsTabPanel from './components/LogsTabPanel';
import OverviewTabPanel from './components/OverviewTabPanel';
import SettingsTabPanel from './components/SettingsTabPanel';
import VolumesTabPanel from './components/VolumesTabPanel';
import { useContainerMutations } from './hooks/useContainerMutations';
import { useContainerQuery } from './hooks/useContainerQuery';

const VALID_TABS = new Set(['overview', 'logs', 'builds', 'domains', 'volumes', 'settings']);

interface ContainerUnavailableProps {
    reason: 'noAccess' | 'error';
    /** Shown as a retry button; pass it for `error`. */
    onRetry?: () => void;
    isRetrying?: boolean;
}

function ContainerUnavailable({ reason, onRetry, isRetrying }: ContainerUnavailableProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { project } = useActiveProject();
    const title = t('breadcrumbs.containerEngine', 'Container Engine');
    const headingRef = usePageHeading(title);
    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumbs.product', 'Products'), href: '#' },
        { title, href: '/container-engine' },
    ]);

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-start gap-4">
            <Breadcrumbs separatorMargin="xs" className="mb-2">
                {breadcrumbItems}
            </Breadcrumbs>
            <div className="flex flex-row flex-wrap items-center gap-3">
                <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-bold">
                    {title}
                </h1>
                {reason === 'noAccess' && <AccessBadge level="none" />}
            </div>
            {reason === 'noAccess' ? (
                <p className="max-w-[80ch] text-base text-fg/78">
                    {project
                        ? t('noAccess.message', { project: project.name })
                        : t('noAccess.messageNoProject')}
                </p>
            ) : (
                <p role="alert" className="max-w-[80ch] text-base text-fg/78">
                    {t('projects:common.loadFailed')}
                </p>
            )}
            <div className="flex flex-wrap gap-2">
                {onRetry && (
                    <Button size="lg" onClick={onRetry} isLoading={isRetrying}>
                        {t('projects:common.retry')}
                    </Button>
                )}
                <Button size="lg" variant="secondary" href="/container-engine">
                    {t('noAccess.back')}
                </Button>
                {reason === 'noAccess' && (
                    <Button size="lg" variant="secondary" href="/projects">
                        {t('projects:switcher.manage')}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function ContainerEngineInstancePage() {
    const { t } = useTranslation('containerEngineInstance');
    const { containerId } = useParams<{ containerId: string }>();

    const {
        query: { data: container, error, isError, refetch, isRefetching },
        probe,
        retryProbe,
    } = useContainerQuery(containerId);
    const containerAccess = useProductAccess('container');
    const canManage = containerAccess === 'manage';
    const { canMove, requestMove, moveModal } = useMoveResource('container', {
        triggerStaysAfterMove: true,
    });
    const errorStatus = (error as { status?: number } | null)?.status;
    const isShown =
        !!container && containerAccess !== 'none' && probe !== 'failed' && errorStatus !== 403;
    const headingRef = usePageHeading(
        isShown ? container.name : undefined,
        t('breadcrumbs.containerEngine', 'Container Engine')
    );

    const { restartMutation, redeployMutation, rebuildMutation, deleteMutation } =
        useContainerMutations(containerId);

    const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
        useDisclosure(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = tabParam && VALID_TABS.has(tabParam) ? tabParam : 'overview';

    const canViewLogs = container?.status === 'running';
    const effectiveTab =
        (activeTab === 'logs' && !canViewLogs) || (activeTab === 'settings' && !canManage)
            ? 'overview'
            : activeTab;

    useEffect(() => {
        if (activeTab === 'logs' && !canViewLogs) {
            const params = new URLSearchParams(searchParams);
            params.delete('tab');
            setSearchParams(params, { replace: true });
        }
    }, [activeTab, canViewLogs, searchParams, setSearchParams]);

    const handleTabChange = (next: string | null) => {
        if (!next) return;
        if (next === 'logs' && !canViewLogs) return;
        const params = new URLSearchParams(searchParams);
        if (next === 'overview') {
            params.delete('tab');
        } else {
            params.set('tab', next);
        }
        setSearchParams(params, { replace: true });
    };

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumbs.product', 'Products'), href: '#' },
        {
            title: t('breadcrumbs.containerEngine', 'Container Engine'),
            href: '/container-engine',
        },
        { title: container?.name ?? '...' },
    ]);

    if ((containerAccess === 'none' && probe === 'missing') || errorStatus === 403) {
        return <ContainerUnavailable reason="noAccess" />;
    }

    if (probe === 'failed') {
        return <ContainerUnavailable reason="error" onRetry={retryProbe} />;
    }

    if (!container || containerAccess === 'none') {
        if (isError && errorStatus !== 404 && containerAccess !== 'none') {
            return (
                <ContainerUnavailable
                    reason="error"
                    onRetry={() => void refetch()}
                    isRetrying={isRefetching}
                />
            );
        }
        return (
            <div role="status" className="w-full h-full flex flex-col items-center justify-center">
                <Loader size="lg" type="bars" />
                <span className="sr-only">{t('common:actions.loading')}</span>
            </div>
        );
    }

    const sourceTypeLabel =
        container.source?.type === 'github'
            ? t('header.sourceGithub', 'GitHub source')
            : container.source?.type === 'url'
              ? t('header.sourceImage', 'Container image')
              : container.source?.type === 'inline'
                ? t('header.sourceInline', 'Inline Dockerfile')
                : t('header.sourceUnknown', 'Unknown source');

    const pricingLabel =
        container.pricing_type === 'request_based'
            ? t('header.pricingRequest', 'Request-based')
            : t('header.pricingInstance', 'Instance-based');

    const isAnyMutating =
        restartMutation.isPending ||
        redeployMutation.isPending ||
        rebuildMutation.isPending ||
        deleteMutation.isPending;

    const cancelDelete = () => {
        if (deleteMutation.isPending) return;
        focusRowMenu('container', container.id);
        closeDeleteModal();
    };

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin="xs" className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full h-auto flex flex-col xl:flex-row justify-between items-start xl:items-end gap-2 xl:gap-8">
                    <div className="flex flex-row justify-start items-center gap-3">
                        <Avatar value={container.name} size="64" />
                        <div className="flex flex-col justify-start items-start">
                            <div className="flex flex-row flex-wrap items-center gap-3">
                                <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-bold">
                                    {container.name}
                                </h1>
                                {!canManage && <AccessBadge level="view" />}
                            </div>
                            <div className="text-sm flex flex-row flex-wrap justify-start items-center gap-2 text-fg/68">
                                <ContainerStatusBadge status={container.status} />
                                <span className="text-3xl leading-none">·</span>
                                <span>{pricingLabel}</span>
                                <span className="text-3xl leading-none">·</span>
                                <span>{sourceTypeLabel}</span>
                                <span className="text-3xl leading-none">·</span>
                                <span>
                                    {t('header.created', 'Created {{date}}', {
                                        date: dayjs(container.created_at).format(
                                            'DD MMM YYYY, HH:mm'
                                        ),
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="h-full flex flex-row justify-end items-center gap-2">
                        <Menu withArrow position="bottom-end">
                            <Menu.Target>
                                <Button
                                    {...moveTriggerProps('container', container.id)}
                                    variant="secondary"
                                    disabled={isAnyMutating}
                                    aria-label={t('header.moreActions')}
                                    title={t('header.moreActions')}
                                >
                                    <IconDots size={20} aria-hidden="true" />
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item
                                    leftSection={<IconReload size={18} stroke={1.5} />}
                                    onClick={() => {
                                        void refetch();
                                    }}
                                >
                                    {t('header.refresh', 'Refresh')}
                                </Menu.Item>
                                {canManage && (
                                    <>
                                        <Menu.Item
                                            leftSection={<IconRefresh size={18} stroke={1.5} />}
                                            onClick={() => restartMutation.mutate()}
                                            disabled={restartMutation.isPending}
                                        >
                                            {t('header.restart', 'Restart')}
                                        </Menu.Item>
                                        <Menu.Item
                                            leftSection={<IconRepeat size={18} stroke={1.5} />}
                                            onClick={() => redeployMutation.mutate()}
                                            disabled={redeployMutation.isPending}
                                        >
                                            {t('header.redeploy', 'Redeploy')}
                                        </Menu.Item>
                                        <Menu.Item
                                            leftSection={<IconHammer size={18} stroke={1.5} />}
                                            onClick={() => rebuildMutation.mutate()}
                                            disabled={rebuildMutation.isPending}
                                        >
                                            {t('header.rebuild', 'Rebuild')}
                                        </Menu.Item>
                                        {canMove && (
                                            <Menu.Item
                                                leftSection={
                                                    <IconFolderShare size={18} stroke={1.5} />
                                                }
                                                onClick={() =>
                                                    requestMove({
                                                        id: container.id,
                                                        name: container.name,
                                                    })
                                                }
                                            >
                                                {t('projects:resource.moveToProject')}
                                            </Menu.Item>
                                        )}
                                        <Menu.Divider />
                                        <Menu.Item
                                            color="var(--color-status-danger)"
                                            leftSection={<IconTrash size={18} stroke={1.5} />}
                                            onClick={openDeleteModal}
                                            disabled={deleteMutation.isPending}
                                        >
                                            {t('header.delete', 'Delete')}
                                        </Menu.Item>
                                    </>
                                )}
                            </Menu.Dropdown>
                        </Menu>
                    </div>
                </div>
                <Tabs
                    className="w-full mt-8 space-y-4"
                    value={effectiveTab}
                    onChange={handleTabChange}
                    color="var(--fg)"
                    keepMounted={false}
                >
                    <Tabs.List aria-label={t('tabs.label')}>
                        <Tabs.Tab value="overview" leftSection={<IconLayoutDashboard size={20} />}>
                            {t('tabs.overview', 'Overview')}
                        </Tabs.Tab>
                        <Tabs.Tab
                            value="logs"
                            leftSection={<IconTerminal2 size={20} />}
                            disabled={!canViewLogs}
                            title={
                                canViewLogs
                                    ? undefined
                                    : t(
                                          'tabs.logsDisabled',
                                          'Logs are only available while the container is running.'
                                      )
                            }
                        >
                            {t('tabs.logs', 'Logs')}
                        </Tabs.Tab>
                        <Tabs.Tab value="builds" leftSection={<IconHammer size={20} />}>
                            {t('tabs.builds', 'Builds')}
                        </Tabs.Tab>
                        <Tabs.Tab value="domains" leftSection={<IconWorld size={20} />}>
                            {t('tabs.domains', 'Domains')}
                        </Tabs.Tab>
                        <Tabs.Tab value="volumes" leftSection={<IconDatabase size={20} />}>
                            {t('tabs.volumes', 'Volumes')}
                        </Tabs.Tab>
                        {canManage && (
                            <Tabs.Tab value="settings" leftSection={<IconSettings size={20} />}>
                                {t('tabs.settings', 'Settings')}
                            </Tabs.Tab>
                        )}
                    </Tabs.List>

                    <Tabs.Panel value="overview" className="w-full">
                        <OverviewTabPanel containerId={container.id} container={container} />
                    </Tabs.Panel>
                    <Tabs.Panel value="logs" className="w-full">
                        <LogsTabPanel containerId={container.id} />
                    </Tabs.Panel>
                    <Tabs.Panel value="builds" className="w-full">
                        <BuildsTabPanel containerId={container.id} />
                    </Tabs.Panel>
                    <Tabs.Panel value="domains" className="w-full">
                        <DomainsTabPanel containerId={container.id} canManage={canManage} />
                    </Tabs.Panel>
                    <Tabs.Panel value="volumes" className="w-full">
                        <VolumesTabPanel
                            containerId={container.id}
                            containerName={container.name}
                            canManage={canManage}
                        />
                    </Tabs.Panel>
                    {canManage && (
                        <Tabs.Panel value="settings" className="w-full">
                            <SettingsTabPanel containerId={container.id} container={container} />
                        </Tabs.Panel>
                    )}
                </Tabs>
            </div>
            <Modal
                opened={deleteModalOpened}
                onClose={cancelDelete}
                centered
                size="sm"
                closeOnClickOutside={!deleteMutation.isPending}
                closeOnEscape={!deleteMutation.isPending}
                overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
                transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
                title={
                    <div className="inline-flex items-center gap-2">
                        <IconAlertTriangle size={20} color="#eb5757" />
                        <span className="font-semibold">
                            {t('header.deleteConfirmTitle', 'Delete container?')}
                        </span>
                    </div>
                }
            >
                <div className="w-full flex flex-col gap-4">
                    <p className="text-sm text-fg/78">
                        {t(
                            'header.deleteConfirmBody',
                            'Are you sure you want to delete "{{containerName}}"? This action cannot be undone and all resources will be cleaned up.',
                            { containerName: container.name }
                        )}
                    </p>
                    <div className="w-full flex flex-row justify-end items-center gap-2">
                        <Button
                            variant="secondary"
                            onClick={cancelDelete}
                            disabled={deleteMutation.isPending}
                        >
                            {t('header.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="danger"
                            leftIcon={<IconTrash size={16} stroke={1.5} />}
                            onClick={() =>
                                deleteMutation.mutate(undefined, {
                                    onSuccess: closeDeleteModal,
                                })
                            }
                            isLoading={deleteMutation.isPending}
                        >
                            {t('header.delete', 'Delete')}
                        </Button>
                    </div>
                </div>
            </Modal>
            {moveModal}
        </div>
    );
}
